import { StoreKit, makeSelectFeatureFlag } from '@nx/state';
import type { UnknownAction } from '@reduxjs/toolkit';
import { isNil, omit } from 'lodash-es';

import { getNodesAndRelationships } from '../../services/discovery';
import { log } from '../../services/logging';
import migrate from '../../services/migrate';
import { getSchemaFromPerspective } from '../../services/perspectives/schema';
import { isVersion5OorGreater } from '../../services/versions/versionUtils';
import type { Node, Relationship } from '../../types/graph';
import type { Scene } from '../../types/scene';
import { setShowSceneEmptyState } from '../app/appDuck';
import {
  getDatabase,
  getDbmsId,
  getServerVersion,
  getUserId,
  isPerspectiveSharingAvailable,
} from '../connections/connectionsDuck';
import { setFilterRules } from '../filter/filter';
import { setGdsState } from '../gds/gds';
import { addToInventory, addToVisible } from '../graph/graph.actions';
import type { Persistor } from '../persistence/types';
import {
  SET_CURRENT_PERSPECTIVE,
  getVisibleLabels,
  getVisibleRelationshipTypes,
  removePerspective,
} from '../perspectives/perspectives';
import { getPerspective } from '../perspectives/perspectives.selector';
import { clearState, unsetCurrentPerspective } from '../rootReducer';
import { getEnableCytoscape } from '../settings/settings';
import { isSharedStorageAvailable } from '../shared-storage/shared-storage.api';
import { setSlicerRanges } from '../slicer/slicer';
import localStorage from '../storage/localStorage';
import type { store as RootStore } from '../store';
import { getStyleById } from '../styles/styles';
import type { AppDispatch, RootState } from '../types';
import {
  getLayoutType,
  setLayoutOptions,
  setLayoutType,
  setPanCoordinates,
  setZoomLevel,
} from '../visualization/visualization';
import { getLatestSceneVersion, trasformations } from './migrations';
import { addScene, setCurrentNodePositions, setCurrentSceneId, setSceneLoadingStatus } from './scene';
import { SceneTransformer } from './scene-transformer';
import { getCurrentScene, isStartWithNewSceneActive } from './scene.selectors';
import { actualiseNodeAndRelationshipIds } from './sceneActions';
import { buildScene } from './thunks/create-scene';

const PREFIX = 'neo4j.bloomScene';

export class LocalStorageScenePersistor implements Persistor {
  private readonly sceneTransformer: SceneTransformer;

  constructor() {
    this.sceneTransformer = new SceneTransformer();
  }

  getKey(state: RootState): string | undefined {
    const dbmsId = getDbmsId(state);
    const dbId = getDatabase(state)?.id;
    const userId = getUserId(state);
    const perspectiveId = getPerspective(state)?.id;

    if (!dbmsId || !dbId || !userId || !perspectiveId) {
      log.warn(
        `Missing data to save/load scene - dbmsId: ${dbmsId}, dbId: ${dbId}, userId: ${userId}, perspectiveId:${perspectiveId}`,
      );
      return;
    }
    return `${PREFIX}.${dbmsId}.${dbId}.${userId}.${perspectiveId}`;
  }

  isEligible(state: RootState): boolean {
    const { globalStore } = StoreKit.UNSAFE_getGlobalStoreInstance();
    const hasSceneFeatureFlag = makeSelectFeatureFlag('explore:scene-without-plugin')(globalStore.getState());
    if (!hasSceneFeatureFlag) return false;

    const hasPlugin = Boolean(state?.connections?.procedures && isPerspectiveSharingAvailable(state));
    if (hasPlugin || isSharedStorageAvailable()) {
      return false;
    }

    return true;
  }

  shouldLoadState({ action, state }: { action: UnknownAction | null; state: RootState }): boolean {
    if (!this.isEligible(state)) return false;

    return action?.type === SET_CURRENT_PERSPECTIVE;
  }

  async loadFromStorage({
    store,
    payload,
    action,
  }: {
    store: typeof RootStore;
    payload: object;
    action: UnknownAction;
  }): Promise<object | void> {
    const { dispatch, getState } = store;

    const state = getState();

    const isFirstLogin = isNil(getPerspective(state));

    if (isNil(action)) {
      if (!isFirstLogin) {
        dispatch(unsetCurrentPerspective());
      }
    }

    const scene = await this.loadScenes({ store });
    if (!scene) {
      log.error('Failed to load existing scene or build a new one');
      return payload;
    }

    dispatch(clearState());

    await this.applyScene({ state, scene, dispatch });

    return payload;
  }

  shouldSaveState({ state, action }: { state: RootState; action: UnknownAction }): boolean {
    if (!this.isEligible(state)) return false;

    const hasPerspective = getPerspective(state) !== undefined;
    const hasScene = 'scene' in state && 'scenes' in state.scene && getCurrentScene(state) !== undefined;

    return (hasPerspective && hasScene) || removePerspective.match(action);
  }

  buildEmptyScene(state: RootState): Scene | undefined {
    const sceneVersion = getLatestSceneVersion();
    const userId = getUserId(state);

    if (!userId || !sceneVersion) {
      log.error(`Missing parameters to load scenes - userId: ${userId}, sceneVersion: ${sceneVersion}`);
      return;
    }

    return {
      ...buildScene(userId, sceneVersion),
      name: `Local Scene`,
    };
  }

  async loadScenes({ store }: { store: typeof RootStore }): Promise<Scene | undefined> {
    const state = store.getState();
    const key = this.getKey(state);
    if (!key) return;
    const isStartWithNewScene = isStartWithNewSceneActive(state);

    if (isStartWithNewScene) {
      return this.buildEmptyScene(state);
    }

    const loaded: Scene | null = await localStorage.getItem(key);
    if (loaded) {
      const perspective = getPerspective(state);
      if (!perspective) return;
      const perspectiveStyle = getStyleById(state)(perspective.id);
      const enableCytoscape = getEnableCytoscape(state);

      const migratedScene: Scene = migrate(loaded, trasformations(perspectiveStyle, enableCytoscape), loaded.version);
      return migratedScene;
    }

    return this.buildEmptyScene(state);
  }

  async applyScene({
    state,
    scene,
    dispatch,
  }: {
    state: RootState;
    scene: Scene;
    dispatch: AppDispatch;
  }): Promise<void> {
    dispatch(setSceneLoadingStatus('loading'));

    try {
      const perspective = getPerspective(state);
      if (!perspective) {
        throw Error('Could not find the perspective for loading the scene');
      }
      const isV5OrGreater = isVersion5OorGreater(getServerVersion(state)) ?? false;

      const inventory = await new Promise<{ nodes: Node[]; relationships: Relationship[] }>((resolve, reject) =>
        getNodesAndRelationships({
          nodeIds: (scene.nodes ?? []).map((node) => node.id),
          relationshipIds: scene.relationships ?? [],
          schema: getSchemaFromPerspective(perspective),
          visibleRelationshipTypes: getVisibleRelationshipTypes(state),
          visibleLabels: getVisibleLabels(state),
          responseHandler: (result: any) => {
            const { nodes, relationships, error } = result;
            if (error) {
              log.error(error.message || error);
              reject(error.message || error);
            } else {
              resolve({ nodes, relationships });
            }
          },
          isV5OrGreater,
        }),
      ).catch((e) => log.error('Failed to load nodes and relationships', e));
      if (!inventory) {
        throw Error('Failed to load nodes and relationships');
      }
      const { nodes, relationships } = inventory;
      const { gds, filters, ranges } = scene;

      const { actualVisibleNodeIds, actualVisibleRelationshipIds, actualPositionNodes } =
        actualiseNodeAndRelationshipIds(
          nodes,
          relationships,
          scene.relationships ?? [],
          scene.nodes ?? [],
          isV5OrGreater,
        );

      dispatch(addScene(omit(scene, ['nodes', 'relationships', 'visualisation', 'filters', 'gds', 'style', 'ranges'])));
      dispatch(setCurrentSceneId(scene.id));

      const { visualisation } = scene;
      const layoutType = getLayoutType(state);

      if (visualisation) {
        dispatch(setZoomLevel(visualisation.zoomLevel));
        if (visualisation.panCoordinates) {
          dispatch(setPanCoordinates(visualisation.panCoordinates));
        }
        if (layoutType !== visualisation.layoutType) {
          dispatch(setLayoutType(visualisation.layoutType));
        }
        if (visualisation.layoutOptions) {
          dispatch(setLayoutOptions(visualisation.layoutOptions));
        }
      }

      dispatch(
        addToInventory({
          nodes,
          relationships,
        }),
      );
      dispatch(
        addToVisible({
          nodeIds: actualVisibleNodeIds,
          relationshipIds: actualVisibleRelationshipIds,
          categories: perspective.categories,
        }),
      );
      if (actualVisibleNodeIds.length > 0) {
        dispatch(setShowSceneEmptyState(false));
      }

      if (filters) {
        dispatch(setFilterRules(filters));
      }
      if (ranges) {
        dispatch(setSlicerRanges(ranges));
      }
      if (gds) {
        dispatch(setGdsState(gds));
      }

      dispatch(setCurrentNodePositions(actualPositionNodes));
      dispatch(setSceneLoadingStatus('inventory'));
    } catch (e: unknown) {
      log.error(e instanceof Error ? e.message : e);
      dispatch(setSceneLoadingStatus('idle'));
    }
  }

  async removeScenesByPerspectiveId(perspectiveId: string): Promise<void> {
    try {
      const allKeys = await localStorage.keys();
      const pattern = new RegExp(`^${PREFIX}\\..*\\..*\\..*\\.${perspectiveId}$`);
      const orphanedSceneKeys = allKeys.filter((key) => pattern.test(key));

      for (const key of orphanedSceneKeys) {
        await localStorage.removeItem(key);
      }
    } catch (e: unknown) {
      log.warn(`Failed to remove scenes on perspective deletion: ${e}`);
    }
  }

  async saveToStorage({ state, action }: { state: RootState; action: UnknownAction }): Promise<void> {
    if (removePerspective.match(action)) {
      await this.removeScenesByPerspectiveId(action.payload.perspectiveId);
      return;
    }

    const scene = getCurrentScene(state);
    if (!scene) {
      log.error(`Missing scene data to save scene: ${scene}`);
      return;
    }
    const fullScene = this.sceneTransformer.getFullSceneFromCurrentGraph(state, scene);
    const key = this.getKey(state);
    if (!key) {
      log.error('Missing key to save scene data');
      return;
    }

    try {
      await localStorage.setItem(key, fullScene);
    } catch (e) {
      log.error(`Unable to save scene: ${e}`);
    }
  }
}
